import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/authContext';
import { doCreateUserInApp } from '../../firebase/auth';
import { onUsersChange, updateUserRole, deactivateUserAccount } from '../../firebase/users';
import { onProjectsChange, updateProjectPermissions } from '../../firebase/projects';
import { Plus, X, Trash2, Briefcase, Folder, Film } from 'lucide-react';

const AddUserModal = ({ isOpen, onClose, currentUser }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const availableRoles = useMemo(() => ({
        superadmin: ['admin', 'collaborator', 'customer'],
        admin: ['collaborator', 'customer'],
        collaborator: ['customer'],
    }[currentUser.role] || []), [currentUser.role]);
    
    useEffect(() => {
        if (isOpen && availableRoles.length > 0) {
            setRole(availableRoles[0]);
        }
    }, [isOpen, availableRoles]);

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setRole(availableRoles.length > 0 ? availableRoles[0] : '');
        setError('');
        setSuccess('');
    }

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!role) {
            setError('Please select a role.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        setSuccess('');

        const result = await doCreateUserInApp(email, password, role, currentUser);

        if (result.success) {
            setSuccess(`Successfully created user ${email}.`);
            resetForm();
            setTimeout(() => {
                setSuccess('');
                onClose();
            }, 1500);
        } else {
            setError(result.error.message);
        }
        setIsSubmitting(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md relative">
                <button onClick={handleClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <form onSubmit={handleCreateUser} className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Create New User</h2>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength="6"
                            className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Role</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            required
                            className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                        >
                            <option value="" disabled>Select a role</option>
                            {availableRoles.map(r => (
                                <option key={r} value={r} className="capitalize dark:bg-gray-700 dark:text-gray-300">{r}</option>
                            ))}
                        </select>
                    </div>
                    {error && <p className="text-red-600 text-sm">{error}</p>}
                    {success && <p className="text-green-600 text-sm">{success}</p>}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full px-4 py-2 text-white font-medium rounded-lg ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {isSubmitting ? 'Creating User...' : 'Create User'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const DeactivateUserModal = ({ isOpen, onClose, onConfirm, user, isDeactivating }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirm Deactivation</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Are you sure you want to deactivate "{user?.displayName || user?.email}"? This will prevent them from logging in.
                </p>
                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isDeactivating}
                        className="px-4 py-2 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeactivating}
                        className="px-4 py-2 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                    >
                        {isDeactivating ? 'Deactivating...' : 'Deactivate'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ManageProjectAccessModal = ({ isOpen, onClose, targetUser, currentUser }) => {
    const [projects, setProjects] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen || !currentUser || !targetUser) return;

        setIsLoading(true);
        const unsub = onProjectsChange(currentUser.uid, (ownedProjects) => {
            const adminProjects = ownedProjects.filter(p => p.userId === currentUser.uid);
            setProjects(adminProjects);
            
            const initialPermissions = {};
            adminProjects.forEach(p => {
                initialPermissions[p.id] = {
                    files: p.permissions?.[targetUser.id]?.files || false,
                    moodboard: p.permissions?.[targetUser.id]?.moodboard || false,
                }
            });
            setPermissions(initialPermissions);
            setIsLoading(false);
        });

        return () => unsub();
    }, [isOpen, currentUser, targetUser]);

    const handlePermissionChange = (projectId, permissionType, value) => {
        setPermissions(prev => ({
            ...prev,
            [projectId]: {
                ...prev[projectId],
                [permissionType]: value
            }
        }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError('');
        try {
            const promises = Object.entries(permissions).map(([projectId, perms]) => 
                updateProjectPermissions(projectId, targetUser.id, perms)
            );
            await Promise.all(promises);
            onClose();
        } catch (err) {
            setError('Failed to update permissions. Please try again.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Manage Project Access</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Grant <span className="font-bold">{targetUser?.displayName}</span> access to your projects.
                    </p>
                    {isLoading ? <p>Loading projects...</p> : (
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                            {projects.length > 0 ? projects.map(project => (
                                <div key={project.id} className="p-3 border rounded-lg dark:border-gray-700">
                                    <h3 className="font-semibold text-gray-800 dark:text-gray-200">{project.name}</h3>
                                    <div className="flex items-center space-x-6 mt-2">
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input type="checkbox"
                                                checked={permissions[project.id]?.files || false}
                                                onChange={(e) => handlePermissionChange(project.id, 'files', e.target.checked)}
                                                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                            />
                                            <Folder className="h-4 w-4 text-blue-500" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Files</span>
                                        </label>
                                         <label className="flex items-center space-x-2 cursor-pointer">
                                            <input type="checkbox"
                                                checked={permissions[project.id]?.moodboard || false}
                                                onChange={(e) => handlePermissionChange(project.id, 'moodboard', e.target.checked)}
                                                className="h-4 w-4 rounded text-purple-600 focus:ring-purple-500"
                                            />
                                            <Film className="h-4 w-4 text-purple-500" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">Moodboard</span>
                                        </label>
                                    </div>
                                </div>
                            )) : <p className="text-sm text-gray-500">You don't own any projects to share.</p>}
                        </div>
                    )}
                    {error && <p className="text-red-600 text-sm">{error}</p>}
                    <div className="flex justify-end pt-4">
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || isLoading}
                            className={`px-4 py-2 text-white font-medium rounded-lg ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Permissions'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Users = () => {
    const { currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
    const [userToDeactivate, setUserToDeactivate] = useState(null);
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const [userToManage, setUserToManage] = useState(null);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [notification, setNotification] = useState({ type: '', message: ''});
    
    useEffect(() => {
        if (!currentUser) return;
        
        const unsubscribe = onUsersChange(currentUser, (newUsers) => {
            let finalUsers = newUsers;
            const userInList = newUsers.some(user => user.id === currentUser.uid);

            if (currentUser.role !== 'superadmin' && !userInList) {
                const currentUserAsListItem = { ...currentUser, id: currentUser.uid };
                finalUsers = [currentUserAsListItem, ...newUsers];
            }

            setUsers(finalUsers);
        }, (error) => {
            setNotification({ type: 'error', message: `Error fetching users: ${error.message}` });
        });

        return () => unsubscribe();
    }, [currentUser]);

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateUserRole(userId, newRole);
            setNotification({ type: 'success', message: 'User role updated successfully.'});
        } catch (error) {
            setNotification({ type: 'error', message: `Failed to update role: ${error.message}`});
        }
        setTimeout(() => setNotification({ type: '', message: ''}), 3000);
    };

    const handleDeactivateRequest = (user) => {
        setUserToDeactivate(user);
        setIsDeactivateModalOpen(true);
    };
    
    const handleManageAccess = (user) => {
        setUserToManage(user);
        setIsAccessModalOpen(true);
    }

    const handleConfirmDeactivate = async () => {
        if (!userToDeactivate) return;
        setIsDeactivating(true);
        try {
            await deactivateUserAccount(userToDeactivate.id);
            setNotification({ type: 'success', message: 'User deactivated successfully.' });
        } catch (error) {
            setNotification({ type: 'error', message: `Failed to deactivate user: ${error.message}`});
        } finally {
            setIsDeactivating(false);
            setIsDeactivateModalOpen(false);
            setUserToDeactivate(null);
            setTimeout(() => setNotification({ type: '', message: ''}), 3000);
        }
    };

    const hierarchy = {
        superadmin: 3,
        admin: 2,
        collaborator: 1,
        customer: 0
    };

    const canEditRole = (targetUser) => {
        if (!currentUser || targetUser.id === currentUser.uid) return false;
        const currentUserLevel = hierarchy[currentUser.role] || -1;
        const targetUserLevel = hierarchy[targetUser.role] || -1;
        return currentUserLevel > targetUserLevel && targetUser.role !== 'superadmin';
    };

    const canDeactivateUser = (targetUser) => {
       return canEditRole(targetUser);
    };

    const availableRolesToAssign = {
        superadmin: ['admin', 'collaborator', 'customer'],
        admin: ['collaborator', 'customer'],
        collaborator: ['customer']
    }[currentUser?.role] || [];
    
    const activeUsers = users.filter(user => user.status !== 'inactive');

    return (
        <div className="w-full h-full flex flex-col pt-16">
            <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8 w-full max-w-4xl mx-auto mb-6 flex-shrink-0">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">Users</h1>
                {currentUser?.role !== 'customer' && (
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors"
                        aria-label="Add new user"
                    >
                        <Plus className="h-5 w-5" />
                        <span className="hidden sm:inline">Add User</span>
                    </button>
                )}
            </div>

            {notification.message && (
                 <div className="px-4 sm:px-6 lg:px-8 w-full max-w-4xl mx-auto mb-6 flex-shrink-0">
                    <div className={`p-4 rounded-md text-sm ${notification.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                        {notification.message}
                    </div>
                </div>
            )}
            
            <div className="flex-grow min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-4xl mx-auto">
                    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-x-auto border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Name</th>
                                    <th scope="col" className="px-6 py-3">Email</th>
                                    <th scope="col" className="px-6 py-3">Role</th>
                                    <th scope="col" className="px-6 py-3">Joined</th>
                                    <th scope="col" className="px-6 py-3"><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeUsers.map((user) => (
                                    <tr key={user.id} className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                            {user.displayName || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4">{user.email}</td>
                                        <td className="px-6 py-4">
                                            {canEditRole(user) ? (
                                                <select
                                                    value={user.role}
                                                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                    className="capitalize bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                >
                                                    {[...new Set([user.role, ...availableRolesToAssign])].map(r => (
                                                        <option key={r} value={r} className="capitalize">{r}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="capitalize px-2.5 py-1 rounded-md text-sm font-medium bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300">{user.role}</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {user.createdAt?.toDate ? new Date(user.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-right flex items-center justify-end space-x-1">
                                             {canEditRole(user) && (
                                                <button onClick={() => handleManageAccess(user)} className="p-2 rounded-md text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-blue-400 transition-colors" aria-label="Manage Project Access">
                                                    <Briefcase className="h-4 w-4" />
                                                </button>
                                            )}
                                            {canDeactivateUser(user) && (
                                                <button onClick={() => handleDeactivateRequest(user)} className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-red-400 transition-colors" aria-label="Deactivate User">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {currentUser && <AddUserModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                currentUser={currentUser}
            />}
            <DeactivateUserModal
                isOpen={isDeactivateModalOpen}
                onClose={() => setIsDeactivateModalOpen(false)}
                onConfirm={handleConfirmDeactivate}
                user={userToDeactivate}
                isDeactivating={isDeactivating}
            />
            {currentUser && userToManage && <ManageProjectAccessModal
                isOpen={isAccessModalOpen}
                onClose={() => { setIsAccessModalOpen(false); setUserToManage(null); }}
                targetUser={userToManage}
                currentUser={currentUser}
            />}
        </div>
    );
};

export default Users;
